<template>
  <div class="league-table">
    <div class="table-header">
      <div class="header-cell communities-col">
        <i class="fa-regular fa-trophy"></i>
        <div class="header-text">{{ $t('broker.leagues.communities') || 'Communities' }}</div>
      </div>
      <div class="header-cell volume-col">
        <div class="header-text">{{ $t('broker.leagues.volume') || 'Volume' }}</div>
      </div>
      <div v-if="hasPointsData" class="header-cell points-col">
        <div class="header-text">{{ $t('broker.leagues.points') || 'Points' }}</div>
      </div>
    </div>

    <div class="table-body">
      <div
        v-for="(community, index) in paginatedData"
        :key="community.id"
        :class="['table-row', { 'highlighted-row': highlightClubId && community.id === highlightClubId }]"
      >
        <div class="cell communities-col">
          <span class="rank-number">{{ (currentPage - 1) * itemsPerPage + index + 1 }}.</span>
          <img :src="community.avatar" :alt="community.name" class="community-avatar" />
          <span class="community-name">{{ community.name }}</span>
        </div>
        <div class="cell volume-col">{{ formatNumber(community.volume) }}</div>
        <div v-if="hasPointsData" class="cell points-col">{{ formatPointsAbbrev(community.points) }}</div>
      </div>
    </div>

    <div class="table-footer">
      <div class="pagination-info">
        {{ paginationInfo }}
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          <i class="fa-solid fa-chevron-left"></i>
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          :class="['page-btn', 'page-number', { active: currentPage === page, ellipsis: page === '...' }]"
          :disabled="page === '...'"
          @click="page !== '...' && changePage(page)"
        >
          {{ page }}
        </button>

        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
      <div class="items-per-page">
        <select v-model.number="itemsPerPage" class="items-select">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeagueTable',
  props: {
    communities: {
      type: Array,
      required: true,
      // Expected: [{ id, name, avatar, volume, points }]
    },
    primaryColor: {
      type: String,
      default: '#0066FF'
    },
    highlightClubId: {
      type: Number,
      default: null
    }
  },
  mounted() {
    console.log('LeagueTable primaryColor prop:', this.primaryColor);
    console.log('LeagueTable headerTextColor:', this.headerTextColor);
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10
    };
  },
  computed: {
    hasPointsData() {
      // Check if any community has points data
      return this.communities.some(c => c.points !== null && c.points !== undefined);
    },
    gridColumns() {
      // Adjust grid columns based on whether points data is available
      return this.hasPointsData ? '2fr 1fr 1fr' : '2fr 1fr';
    },
    headerTextColor() {
      // Calculate YIQ contrast to determine if text should be black or white
      const color = this.primaryColor.replace('#', '');
      const r = parseInt(color.substr(0, 2), 16);
      const g = parseInt(color.substr(2, 2), 16);
      const b = parseInt(color.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return yiq >= 128 ? '#000000' : '#FFFFFF';
    },
    totalPages() {
      return Math.ceil(this.communities.length / this.itemsPerPage);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.communities.slice(start, end);
    },
    paginationInfo() {
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.communities.length);
      return `${start}-${end} of ${this.communities.length}`;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;

      if (this.totalPages <= maxVisible + 2) {
        // Show all pages if total is small
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        if (this.currentPage > 3) {
          pages.push('...');
        }

        // Show pages around current
        const start = Math.max(2, this.currentPage - 1);
        const end = Math.min(this.totalPages - 1, this.currentPage + 1);

        for (let i = start; i <= end; i++) {
          if (!pages.includes(i)) {
            pages.push(i);
          }
        }

        if (this.currentPage < this.totalPages - 2) {
          pages.push('...');
        }

        // Always show last page
        if (!pages.includes(this.totalPages)) {
          pages.push(this.totalPages);
        }
      }

      return pages;
    }
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    formatNumber(num) {
      if (!num) return '0';
      return num.toLocaleString('en-US');
    },
    formatPointsAbbrev(num) {
      if (!num) return '0';
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      }
      if (num >= 1000) {
        return Math.floor(num / 1000) + 'k';
      }
      return num.toString();
    }
  }
};
</script>

<style lang="scss" scoped>
.league-table {
  background: transparent;
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: v-bind(gridColumns);
  background: v-bind(primaryColor);
  color: v-bind(headerTextColor);
  padding: 16px 24px;
  font-family: 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 700;

  .header-cell {
    display: flex;
    align-items: center;
    gap: 8px;

    &.volume-col,
    &.points-col {
      justify-content: center;
    }

    i {
      font-size: 18px;
      color: inherit;
    }

    .header-text {
      color: inherit;
      font-weight: inherit;
    }
  }
}

.table-body {
  .table-row {
    display: grid;
    grid-template-columns: v-bind(gridColumns);
    padding: 16px 24px;
    border-bottom: 1px solid #D0D0D0;
    transition: background-color 0.15s ease;
    background-color: transparent;

    &:hover {
      background-color: #F8F8F8;
    }

    &:last-child {
      border-bottom: none;
    }

    &.highlighted-row {
      position: relative;
      border-left: 4px solid v-bind(primaryColor);
      padding-left: 20px;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: v-bind(primaryColor);
        opacity: 0.08;
        pointer-events: none;
      }

      &:hover::before {
        opacity: 0.12;
      }

      .community-name {
        font-weight: 700;
        color: v-bind(primaryColor);
      }
    }

    .cell {
      display: flex;
      align-items: center;
      font-family: 'Avenir', sans-serif;
      font-size: 15px;
      color: #333333;
      position: relative;
      z-index: 1;

      &.communities-col {
        gap: 12px;
      }

      &.volume-col,
      &.points-col {
        justify-content: center;
        font-weight: 500;
      }
    }

    .rank-number {
      min-width: 28px;
      color: #666666;
      font-weight: 500;
    }

    .community-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid #E8E8E8;
    }

    .community-name {
      font-weight: 500;
      color: #1A1A1A;
    }
  }
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #D0D0D0;
  background: transparent;
}

.pagination-info {
  font-family: 'Avenir', sans-serif;
  font-size: 14px;
  color: #666666;
}

.pagination-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-btn {
  min-width: 36px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E0E0E0;
  background: #FFFFFF;
  border-radius: 50%;
  font-family: 'Avenir', sans-serif;
  font-size: 14px;
  color: #333333;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled):not(.active):not(.ellipsis) {
    background: v-bind(primaryColor);
    color: v-bind(headerTextColor);
    border-color: v-bind(primaryColor);
    opacity: 0.85;
  }

  &.active {
    background: v-bind(primaryColor);
    color: v-bind(headerTextColor);
    border-color: v-bind(primaryColor);
    font-weight: 600;
    opacity: 1;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.ellipsis {
    border: none;
    background: transparent;
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  i {
    font-size: 12px;
  }
}

.items-per-page {
  .items-select {
    padding: 8px 32px 8px 12px;
    border: 1px solid #E0E0E0;
    border-radius: 6px;
    background: #FFFFFF;
    font-family: 'Avenir', sans-serif;
    font-size: 14px;
    color: #333333;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 8px center;
    background-size: 16px;

    &:hover {
      border-color: #CCCCCC;
    }

    &:focus {
      outline: none;
      border-color: #0066FF;
    }
  }
}

.theme--dark {
  .league-table {
    background: transparent;
  }

  .table-body .table-row {
    border-bottom-color: #3A3A3A;
    background-color: transparent;

    &:hover {
      background-color: #252525;
    }

    .cell {
      color: #CCCCCC;
    }

    .rank-number {
      color: #888888;
    }

    .community-name {
      color: #FFFFFF;
    }

    .community-avatar {
      border-color: #333333;
    }
  }

  .table-footer {
    border-top-color: #3A3A3A;
    background: transparent;
  }

  .pagination-info {
    color: #888888;
  }

  .page-btn {
    background: #2A2A2A;
    border-color: #333333;
    color: #CCCCCC;

    &:hover:not(:disabled):not(.active):not(.ellipsis) {
      background: v-bind(primaryColor);
      color: v-bind(headerTextColor);
      border-color: v-bind(primaryColor);
      opacity: 0.85;
    }

    &.active {
      background: v-bind(primaryColor);
      color: v-bind(headerTextColor);
      border-color: v-bind(primaryColor);
      opacity: 1;
    }
  }

  .items-select {
    background: #2A2A2A;
    border-color: #333333;
    color: #CCCCCC;

    &:hover {
      border-color: #444444;
    }
  }
}
</style>
